import {
  CSSProperties,
  FC,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import cn from 'classnames';

import { LocalizationContext } from 'localization';

import { ShellDesktop } from './ShellDesktop';
import s from './ShellOverlay.module.scss';

export const SHELL_OPEN_SPLIT_POSITION = 20;
export const SHELL_CLOSED_SPLIT_POSITION = 100;

interface IShellOverlayProps {
  isOpen: boolean;
  splitPosition: number;
  setSplitPosition: (position: number) => void;
}

interface IShellOverlayStyle extends CSSProperties {
  '--ss-shell-split-left': string;
}

export const ShellOverlay: FC<IShellOverlayProps> = ({
  isOpen,
  splitPosition,
  setSplitPosition,
}) => {
  const { t } = useContext(LocalizationContext);
  const [isDragging, setIsDragging] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const updateSplitPosition = useCallback(
    (clientX: number): void => {
      const overlay = overlayRef.current;

      if (!overlay) return;

      const { left, width } = overlay.getBoundingClientRect();

      if (width <= 0) return;

      setSplitPosition(((clientX - left) / width) * 100);
    },
    [setSplitPosition]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent): void => {
      updateSplitPosition(event.clientX);
    };
    const handleMouseUp = (): void => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, updateSplitPosition]);

  const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (event.button !== 0) return;

    event.preventDefault();
    updateSplitPosition(event.clientX);
    setIsDragging(true);
  };

  const visibleSplitPosition = isOpen
    ? splitPosition
    : SHELL_CLOSED_SPLIT_POSITION;
  const isAtLeftEdge = isOpen && visibleSplitPosition === 0;
  const style: IShellOverlayStyle = {
    '--ss-shell-split-left': `${visibleSplitPosition}%`,
  };

  return (
    <div
      ref={overlayRef}
      className={cn(
        s.ssShellOverlay,
        isOpen && s.ssShellOverlay_visible,
        isDragging && s.ssShellOverlay_dragging
      )}
      style={style}
      data-testid="ss-shell-overlay"
    >
      <aside className={s.ssShellClient} aria-label={t('shell.client')}>
        {isOpen && <ShellDesktop />}
      </aside>
      {isOpen && (
        <div
          className={cn(
            s.ssShellSplitter,
            isAtLeftEdge && s.ssShellSplitter_atLeftEdge
          )}
          role="group"
          aria-label={t('shell.splitter')}
          onMouseDown={handleMouseDown}
        >
          <span className={s.ssShellSplitterLine} aria-hidden />
          <div className={s.ssShellSplitterControls}>
            <button
              className={s.ssShellSplitterButton}
              type="button"
              aria-label={t('shell.splitter.toLeft')}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => setSplitPosition(0)}
            >
              <span className={s.ssShellSplitterArrow_left} aria-hidden />
            </button>
            <button
              className={s.ssShellSplitterButton}
              type="button"
              aria-label={t('shell.splitter.toRight')}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() =>
                setSplitPosition(
                  isAtLeftEdge
                    ? SHELL_OPEN_SPLIT_POSITION
                    : SHELL_CLOSED_SPLIT_POSITION
                )
              }
            >
              <span className={s.ssShellSplitterArrow_right} aria-hidden />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
